import { useState, useEffect, useCallback } from 'react';
import { publicQuotesAPI } from '../utils/api';

interface QuoteData {
  id: number;
  number: string;
  issueDate: string;
  validUntil: string;
  // Company information (optional chaining car peut être null)
  Company?: {
    tradeName: string;
    legalName: string;
    address: string;
    city: string;
    postalCode?: string;
    phone?: string;
    email?: string;
    registrationNumber?: string;
    logoUrl?: string;
    quoteColor?: string;
    legalForm?: string;
    capital?: string;
    vatNumber?: string;
    rcsCity?: string;
    rcsNumber?: string;
    apeCode?: string;
    iban?: string;
    bic?: string;
    country?: string;
    vatRegime?: string;
    vatExemptArticle?: string;
  };
  // Client information (optional chaining car peut être null)
  Client?: {
    fullName?: string;
    companyName?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    email?: string;
    phone?: string;
    vatNumber?: string;
    country?: string;
  };
  // Quote details
  title?: string;  // ✅ Aligné sur Prisma (remplace eventTitle)
  items: Array<{
    description?: string;
    unitPrice: number;
    quantity: number;
    totalHT: number;
    totalTTC: number;
    taxPercent: number;
  }>;
  totalHT: number;
  discountType?: string;
  discountValue?: number;
  discountAmount?: number;
  totalTVA: number;
  totalTTC: number;
  status: string;
  internalNotes?: string;
  termsConditions?: string;
  acceptedAt?: string;
  // Template et couleurs configurés par la company
  templatePreference?: {
    colorPrimary?: string;
    colorSecondary?: string;
    useCompanyColors?: boolean;
    logoPosition?: string;
  } | null;
  template?: {
    id: string;
    name: string;
    displayName?: string;
    styleConfig?: any;
    layoutConfig?: any;
  } | null;
}

interface AcceptQuoteParams {
  signature: string | undefined;
  signedByName: string;
  signedByEmail: string | undefined;
}

/**
 * Hook custom pour gérer l'affichage et l'acceptation d'un devis public
 *
 * @param token - Token du lien public
 * @returns État et fonctions pour gérer le devis public
 *
 * @example
 * ```tsx
 * const { quote, loading, error, accepting, accepted, loadQuote, acceptQuote } = usePublicQuote(token);
 *
 * if (loading) return <Loader />;
 * if (error) return <Error message={error} />;
 *
 * const handleAccept = async (signature: string) => {
 *   const success = await acceptQuote({
 *     signature,
 *     signedByName: quote.Client?.fullName || quote.Client?.companyName || 'N/A',
 *     signedByEmail: quote.Client?.email,
 *   });
 *   if (success) setAccepted(true);
 * };
 * ```
 */
export function usePublicQuote(token: string | undefined) {
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  /**
   * Normaliser les données du devis (conversion string → number)
   */
  const normalizeQuote = useCallback((data: any): QuoteData => {
    return {
      ...data,
      totalHT: parseFloat(data.totalHT) || 0,
      totalTVA: parseFloat(data.totalTVA) || 0,
      totalTTC: parseFloat(data.totalTTC) || 0,
      discountAmount: data.discountAmount ? parseFloat(data.discountAmount) : undefined,
      items: data.items.map((item: any) => ({
        description: item.description || '',
        unitPrice: parseFloat(item.unitPrice) || 0,
        quantity: parseFloat(item.quantity) || 0,
        totalHT: parseFloat(item.totalHT) || 0,
        totalTTC: parseFloat(item.totalTTC) || 0,
        taxPercent: parseFloat(item.taxPercent) || 0,
      }))
    };
  }, []);

  /**
   * Charger le devis public depuis l'API
   */
  const loadQuote = useCallback(async () => {
    if (!token) {
      setError('Token manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // ✅ Utilisation de l'API Layer
      const data = await publicQuotesAPI.getQuote(token);

      // Normaliser et stocker les données
      const normalizedQuote = normalizeQuote(data.data);
      setQuote(normalizedQuote);

      // Vérifier si déjà accepté
      if (data.data.status === 'accepted') {
        setAccepted(true);
      }
    } catch (err) {
      const errorCode = err instanceof Error ? err.message : 'LOAD_ERROR';
      setError(errorCode);
    } finally {
      setLoading(false);
    }
  }, [token, normalizeQuote]);

  /**
   * Accepter le devis avec signature électronique
   *
   * @returns true si succès, false si erreur
   */
  const acceptQuote = useCallback(async (params: AcceptQuoteParams): Promise<boolean> => {
    if (!token || !quote) {
      setError('Données manquantes pour accepter le devis');
      return false;
    }

    try {
      setAccepting(true);
      setError(null);

      // ✅ Utilisation de l'API Layer (camelCase = convention Prisma)
      await publicQuotesAPI.acceptQuote(token, {
        acceptedVia: 'web',
        timestamp: new Date().toISOString(),
        signature: params.signature,
        signedByName: params.signedByName,
        signedByEmail: params.signedByEmail,
      });

      setAccepted(true);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      return false;
    } finally {
      setAccepting(false);
    }
  }, [token, quote]);

  // Charger le devis au montage du composant
  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  return {
    quote,
    loading,
    error,
    accepting,
    accepted,
    loadQuote,
    acceptQuote,
  };
}
